import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Box,
} from '@mui/material';
import {
  Error,
  Warning,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import type { SystemAlert } from '../../services/superAdminService';

interface SystemAlertsProps {
  alerts: SystemAlert[];
}

const SystemAlerts: React.FC<SystemAlertsProps> = ({ alerts }) => {
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <Error />;
      case 'warning':
        return <Warning />;
      case 'info':
        return <Info />;
      case 'success':
        return <CheckCircle />;
      default:
        return <Info />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'error.main';
      case 'warning':
        return 'warning.main';
      case 'info':
        return 'info.main';
      case 'success':
        return 'success.main';
      default:
        return 'grey.500';
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          System Alerts
        </Typography>
        <List>
          {Array.isArray(alerts) && alerts.map((alert, index) => (
            <ListItem key={alert.id} divider={index < alerts.length - 1}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: getAlertColor(alert.type) }}>
                  {getAlertIcon(alert.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={alert.title}
                secondary={
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {alert.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(alert.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Chip 
                  label={alert.priority} 
                  size="small" 
                  color={alert.priority === 'critical' ? 'error' : alert.priority === 'high' ? 'warning' : 'default'}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {(!Array.isArray(alerts) || alerts.length === 0) && (
            <ListItem>
              <ListItemText
                primary="No system alerts"
                secondary="System is running smoothly"
              />
            </ListItem>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default SystemAlerts;
